import { useState, useCallback, useEffect, useMemo } from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Plus, Pencil, Trash2, List, MapPin, Calendar, Briefcase } from 'lucide-react';
import { MonthYearPicker } from './MonthYearPicker';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { detectTextDirection } from '@/lib/textDirection';
import { sortWorkExperiencesNewestFirst } from './experienceSorting';
import { formatLocalizedDate } from '@/lib/localizedDate';

interface Experience {
  id: string;
  title: string;
  employer_name: string | null;
  location: string | null;
  employment_type: string | null;
  start_date: string | null;
  end_date: string | null;
  is_current: boolean | null;
  description: string | null;
}

interface Props {
  experiences: Experience[];
  profileId: string;
  onAdd: (data: Record<string, unknown>) => void;
  onUpdate: (id: string, data: Record<string, unknown>) => void;
  onDelete: (id: string) => void;
  saving: boolean;
  isFreshGraduate?: boolean;
  onToggleFreshGraduate?: (checked: boolean) => void;
  editLocked?: boolean;
}

const empty = { title: '', employer_name: '', location: '', employment_type: 'full-time', start_date: '', end_date: '', is_current: false, description: '' };

function toDateValue(value: string) {
  return value && !/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}-01` : value;
}

function addBulletPoint(text: string): string {
  return text + (text.endsWith('\n') ? '• ' : '\n• ');
}

function formatDate(dateStr: string | null, language: string): string {
  if (!dateStr) return '?';
  try {
    return formatLocalizedDate(dateStr, 'MMM yyyy', language);
  } catch {
    return dateStr;
  }
}

function renderDescription(text: string) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const hasBullets = lines.some(l => /^[\u2022\u2023\u25E6\u2043•*-]\s/.test(l));

  if (!hasBullets) {
    // Treat each line as a bullet point
    return (
      <ul className="text-[13px] text-muted-foreground space-y-1 list-disc ps-4">
        {lines.map((line, i) => (
          <li key={i} className="leading-relaxed">{line}</li>
        ))}
      </ul>
    );
  }

  return (
    <div className="text-[13px] text-muted-foreground space-y-1">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        const isBullet = /^[\u2022\u2023\u25E6\u2043•*-]\s/.test(trimmed);
        const isSubBullet = /^\s{2,}[\u2022\u2023\u25E6\u2043•*-]\s/.test(line);
        if (isBullet) {
          const content = trimmed.replace(/^[\u2022\u2023\u25E6\u2043•*-]\s*/, '');
          return (
            <div key={i} className={`flex gap-2 ${isSubBullet ? 'ms-6' : 'ms-1'}`}>
              <span className="text-seek/50 mt-[2px]">•</span>
              <span className="leading-relaxed">{content}</span>
            </div>
          );
        }
        // Section header lines (e.g. "Selected contributions:")
        if (trimmed && trimmed.endsWith(':')) {
          return <p key={i} className="font-medium text-foreground/80 mt-2 mb-0.5">{trimmed}</p>;
        }
        if (trimmed) return <p key={i} className="leading-relaxed">{trimmed}</p>;
        return null;
      })}
    </div>
  );
}

function formatEmploymentType(type: string | null, t: ReturnType<typeof useTranslation>['t']): string {
  if (!type) return '';
  const keyByType: Record<string, string> = {
    'full-time': 'seek.profile.empType.fullTime',
    'part-time': 'seek.profile.empType.partTime',
    contract: 'seek.profile.empType.contract',
    freelance: 'seek.profile.empType.freelance',
    internship: 'seek.profile.empType.internship',
    temporary: 'seek.profile.empType.temporary',
    volunteer: 'seek.profile.empType.volunteer',
    tameer: 'seek.profile.empType.tameer',
  };
  const key = keyByType[type];
  if (!key) return type.replace(/-/g, ' ');
  const translated = t(key);
  return translated === key ? type.replace(/-/g, ' ') : translated;
}

export function ExperienceSection({ experiences, profileId, onAdd, onUpdate, onDelete, saving, isFreshGraduate, onToggleFreshGraduate, editLocked }: Props) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(empty);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const sortedExperiences = useMemo(
    () => sortWorkExperiencesNewestFirst(experiences),
    [experiences],
  );

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.sectionId === 'section-experience') {
        if (detail.editId) {
          const exp = experiences.find((x: Experience) => x.id === detail.editId);
          if (exp) {
            setEditId(exp.id);
            setForm({
              title: exp.title,
              employer_name: exp.employer_name ?? '',
              location: exp.location ?? '',
              employment_type: exp.employment_type ?? 'full-time',
              start_date: exp.start_date ?? '',
              end_date: exp.end_date ?? '',
              is_current: exp.is_current ?? false,
              description: exp.description ?? '',
            });
            setOpen(true);
            return;
          }
        }
        openNew();
      }
    };
    window.addEventListener('open-section-edit', handler);
    return () => window.removeEventListener('open-section-edit', handler);
  }, [experiences]);

  const openEdit = (exp: Experience) => {
    setEditId(exp.id);
    setForm({
      title: exp.title,
      employer_name: exp.employer_name ?? '',
      location: exp.location ?? '',
      employment_type: exp.employment_type ?? 'full-time',
      start_date: exp.start_date ?? '',
      end_date: exp.end_date ?? '',
      is_current: exp.is_current ?? false,
      description: exp.description ?? '',
    });
    setOpen(true);
  };

  const dateError = (() => {
    if (form.start_date && form.end_date && !form.is_current && form.start_date > form.end_date) {
      return t('seek.profile.startDateAfterEndDate');
    }
    return null;
  })();

  const handleSave = () => {
    if (dateError) return;
    const payload = {
      ...form,
      start_date: toDateValue(form.start_date) || null,
      end_date: form.is_current ? null : (toDateValue(form.end_date) || null),
      candidate_profile_id: profileId,
    };
    if (editId) onUpdate(editId, payload);
    else onAdd(payload);
    setOpen(false);
  };

  const handleAddBullet = useCallback(() => {
    setForm(p => ({ ...p, description: addBulletPoint(p.description) }));
  }, []);

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center justify-between mb-5">
          <h3 className="flex items-center gap-2.5 text-lg font-bold text-foreground font-[var(--font-display)]">
            <Building2 className="w-5 h-5 text-seek" />
            {t('seek.profile.workExperience')}
          </h3>
          <div className="flex items-center gap-3">
            <label className={`flex items-center gap-2 text-sm ${experiences.length > 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
              <Checkbox
                checked={isFreshGraduate}
                disabled={experiences.length > 0}
                onCheckedChange={(checked) => onToggleFreshGraduate?.(!!checked)}
              />
              <span className="text-muted-foreground">{t('seek.profile.freshGraduate')}</span>
            </label>
            {!isFreshGraduate && (
              <Button variant="ghost" size="sm" onClick={openNew} disabled={editLocked} className="gap-1 bg-seek/10 text-seek hover:bg-seek hover:text-white">
                <Plus className="w-4 h-4" /> {t('seek.profile.add')}
              </Button>
            )}
          </div>
        </div>

        {isFreshGraduate ? (
          <p className="text-sm text-seek font-medium">{t('seek.profile.freshGraduateNote')}</p>
        ) : experiences.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('seek.profile.noExperience')}</p>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            {experiences.length > 1 && (
              <div className="absolute top-3 bottom-3 start-[15px] w-px bg-border" />
            )}

            <div className="space-y-0">
              {sortedExperiences.map((exp, idx) => (
                <div key={exp.id} className="group relative">
                  {/* Timeline dot */}
                  <div className="absolute start-[9px] top-[22px] z-10">
                    <div className={`w-3 h-3 rounded-full border-2 ${exp.is_current ? 'bg-seek border-seek' : 'bg-background border-muted-foreground/30'}`} />
                  </div>

                  <div className="ms-10 pb-6">
                    <div className="rounded-xl border border-border/60 bg-card hover:border-seek/20 hover:shadow-sm transition-all p-5">
                      {/* Header row */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <h4 className="font-semibold text-[15px] text-foreground leading-snug" dir={detectTextDirection(exp.title)}>{exp.title}</h4>
                          {exp.employer_name && (
                            <p className="text-sm font-medium text-seek mt-0.5" dir={detectTextDirection(exp.employer_name)}>{exp.employer_name}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => openEdit(exp)} disabled={editLocked}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setDeleteTarget({ id: exp.id, title: exp.title })} disabled={editLocked}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>

                      {/* Meta row */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2.5">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(exp.start_date, language)} — {exp.is_current ? t('seek.profile.present') : formatDate(exp.end_date, language)}</span>
                        </div>
                        {exp.location && (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            <span>{exp.location}</span>
                          </div>
                        )}
                        {exp.employment_type && (
                          <Badge variant="outline" className="text-[10px] font-medium px-2 py-0 h-5 border-border">
                            {formatEmploymentType(exp.employment_type, t)}
                          </Badge>
                        )}
                        {exp.is_current && (
                          <Badge className="text-[10px] font-medium px-2 py-0 h-5 bg-seek/10 text-seek border-seek/20 hover:bg-seek/10">
                            {t('seek.profile.current')}
                          </Badge>
                        )}
                      </div>

                      {/* Description */}
                      {exp.description && (
                        <div className="mt-4 pt-3 border-t border-border/40" dir={detectTextDirection(exp.description)}>
                          {renderDescription(exp.description)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? t('seek.profile.editExperience') : t('seek.profile.addExperience')}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>{t('seek.profile.jobTitle')} *</Label><Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} /></div>
            <div><Label>{t('seek.profile.employer')} *</Label><Input value={form.employer_name} onChange={(e) => setForm(p => ({ ...p, employer_name: e.target.value }))} /></div>
            <div><Label>{t('seek.profile.location')} *</Label><Input value={form.location} onChange={(e) => setForm(p => ({ ...p, location: e.target.value }))} /></div>
            <div>
              <Label>{t('seek.profile.type')} *</Label>
              <Select value={form.employment_type} onValueChange={(v) => setForm(p => ({ ...p, employment_type: v }))}>
                <SelectTrigger><SelectValue placeholder={t('seek.profile.selectType')} /></SelectTrigger>
                <SelectContent>
                  {([
                    { value: 'full-time', label: t('seek.profile.empType.fullTime') },
                    { value: 'part-time', label: t('seek.profile.empType.partTime') },
                    { value: 'contract', label: t('seek.profile.empType.contract') },
                    { value: 'freelance', label: t('seek.profile.empType.freelance') },
                    { value: 'internship', label: t('seek.profile.empType.internship') },
                    { value: 'temporary', label: t('seek.profile.empType.temporary') },
                    { value: 'volunteer', label: t('seek.profile.empType.volunteer') },
                    { value: 'tameer', label: t('seek.profile.empType.tameer') },
                  ]).map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t('seek.profile.startDate')} *</Label><MonthYearPicker value={form.start_date?.substring(0, 7)} onChange={(v) => setForm(p => ({ ...p, start_date: v }))} /></div>
            <div><Label className={form.is_current ? 'text-muted-foreground/50' : ''}>{t('seek.profile.endDate')} {!form.is_current && '*'}</Label><MonthYearPicker value={form.is_current ? '' : form.end_date?.substring(0, 7)} onChange={(v) => setForm(p => ({ ...p, end_date: v, is_current: false }))} disabled={form.is_current} /></div>
            <div className="flex items-end gap-2">
              <Checkbox checked={form.is_current} onCheckedChange={(c) => setForm(p => ({ ...p, is_current: c === true, end_date: c === true ? '' : p.end_date }))} id="is_current" className="border-seek data-[state=checked]:bg-seek data-[state=checked]:text-white" />
              <Label htmlFor="is_current" className="mb-0">{t('seek.profile.currentlyWorking')}</Label>
            </div>
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-1">
                <Label>{t('seek.profile.description')}</Label>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={handleAddBullet}>
                  <List className="w-3.5 h-3.5" /> {t('seek.profile.addBullet')}
                </Button>
              </div>
              <Textarea rows={5} value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder={t('seek.profile.descPlaceholder')} />
              <p className="text-xs text-muted-foreground mt-1">{t('seek.profile.bulletTip')}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            {dateError && <p className="text-xs text-destructive">{dateError}</p>}
            <Button onClick={handleSave} disabled={!form.title || !form.employer_name || !form.location || !form.employment_type || !form.start_date || (!form.is_current && !form.end_date) || !!dateError || saving} className="bg-seek text-white hover:bg-seek/90">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('seek.profile.deleteExperience')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('seek.profile.deleteExperienceConfirm', { title: deleteTarget?.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) { onDelete(deleteTarget.id); setDeleteTarget(null); } }}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
